<template>
  <div class="full-height">
    <div class="container">
      <article class="slds-card">
        <div class="slds-card__header slds-grid">
          <header class="slds-media slds-media_center slds-has-flexi-truncate">
            <div class="slds-media__figure">
              <span class="slds-icon_container slds-icon-standard-event">
                <svg class="slds-icon slds-icon-action-log-this-event" aria-hidden="true">
                  <use xlink:href="../assets/symbols.svg#event" />
                </svg>
              </span>
            </div>
            <div class="slds-media__body">
              <h2 class="slds-card__header-title">
                <span class="slds-card__header-link slds-truncate">
                  {{ $t('sections.executionRules.sendingRules.label') }}
                </span>
              </h2>
            </div>
            <div>
              <div class="slds-button-group" role="group">
                <button class="slds-button slds-button_icon slds-button_icon-border-filled" :class="{'slds-is-selected': showSidebar}" aria-pressed="true" @click="toggleSidebar">
                  <svg class="slds-button__icon" aria-hidden="true">
                    <use xlink:href="../assets/symbols.svg#rows"></use>
                  </svg>
                </button>
              </div>
            </div>
          </header>
        </div>
      </article>
      <div class="row">
        <div class="column">
          <article class="slds-card calendar-wrapper" >
            <div class="full-height">
              <Calendar
                :days="days"
                :disabled="calendarDisabled"
                :elements="filteredTimingSlots"
                @dayClick="handleDayClick"
                ref="calendar"
                view-mode="weekly"
                type="sendingRules"
              />
              <div class="calendar-alert" v-if="calendarDisabled">
                <div class="slds-text-heading_large slds-text-align_center">
                  {{$t('executionRules.sendingRules.selectMarketAndChannel')}}
                </div>
              </div>
            </div>
          </article>
        </div>
        <article class="slds-card sidebar" v-if="showSidebar">
          <div class="slds-card__body slds-card__body_inner">
            <div class="sidebar">
              <Picklist :label="$t('executionRules.sendingRules.calendar.label')" v-model="calendar" :options="calendars" labels-prefix="executionRules.sendingRules.calendar" />
              <template v-if="calendar">
                <Picklist :label="$t('executionRules.sendingRules.market')" v-model="market" :options="markets" labels-prefix="markets" v-if="calendar === 'CC'" />
                <Picklist :label="$t('executionRules.sendingRules.channel')" v-model="channel" :options="channels" labels-prefix="channels" :disabled="false" />
              </template>
              <template v-if="calendar">
                <Input
                  min="1"
                  ref="pressure_day"
                  type="number"
                  v-model="pressureRule.maxCommsWeek"
                  :disabled="calendarDisabled || !checkPermission('MANAGE_EXECUTION_RULE')"
                  :label="$t('executionRules.sendingRules.maxCommsWeek')"
                  :placeholder="calendarDisabled ? $t('executionRules.sendingRules.selectMarketAndChannel'): ''"
                  :validation="['required', 'positive-number']" />
                <Input
                  min="1"
                  ref="pressure_month"
                  type="number"
                  v-model="pressureRule.maxCommsMonth"
                  :disabled="calendarDisabled || !checkPermission('MANAGE_EXECUTION_RULE')"
                  :label="$t('executionRules.sendingRules.maxCommsMonth')"
                  :placeholder="calendarDisabled ? $t('executionRules.sendingRules.selectMarketAndChannel'): ''"
                  :validation="['required', 'positive-number']" />
                <Input
                  min="1"
                  ref="pressure_min_days"
                  type="number"
                  v-model="pressureRule.minDaysBetweenComms"
                  :disabled="calendarDisabled || !checkPermission('MANAGE_EXECUTION_RULE')"
                  :label="$t('executionRules.sendingRules.minDaysBetweenComms')"
                  :placeholder="calendarDisabled ? $t('executionRules.sendingRules.selectMarketAndChannel'): ''"
                  :validation="['required', 'positive-number', { name: 'bigger-or-equal-than', param: 1 }]" />

                <button class="slds-button slds-button_brand" @click="savePressureRule" v-if="checkPermission('MANAGE_EXECUTION_RULE')">{{$t('action.save')}}</button>
              </template>
            </div>
          </div>
        </article>
      </div>
    </div>

    <transition name="fade">
      <Modal
        directional
        v-if="showModal"
        @close="closeModal"
        :title="$t(`executionRules.sendingRules.modal.${selectedSlot.id ? 'edit' : 'add'}Slot`)">
          <Input
            :label="$t('executionRules.sendingRules.modal.inclusionDay')"
            disabled
            v-model="selectedSlot.inclusionDayLabel" />
          <Input
            @blur="handleBlur('sendStartHour', $event)"
            hide-clear-button
            ref="send_startHour"
            v-model="selectedSlot.sendStartHour"
            :disabled="!checkPermission('MANAGE_EXECUTION_RULE')"
            :fixedTextAddonPost="timezone"
            :label="$t('executionRules.sendingRules.modal.sendStartHour')"
            :validation="['required', 'hour', {name: 'is-hour-before', param: selectedSlot.sendEndHour}]" />
          <Input
            @blur="handleBlur('sendEndHour', $event)"
            hide-clear-button
            ref="send_endHour"
            v-model="selectedSlot.sendEndHour"
            :disabled="!checkPermission('MANAGE_EXECUTION_RULE')"
            :fixedTextAddonPost="timezone"
            :label="$t('executionRules.sendingRules.modal.sendEndHour')"
            :validation="['required', 'hour']"/>

          <template slot="footer">
            <button class="slds-button slds-button_destructive" @click="deleteSlot" :disabled="!selectedSlot.id || !checkPermission('MANAGE_EXECUTION_RULE')">{{$t('action.delete')}}</button>
            <button class="slds-button slds-button_neutral" @click="closeModal">{{$t('action.cancel')}}</button>
            <button class="slds-button slds-button_brand" @click="saveSlot" :disabled="!checkPermission('MANAGE_EXECUTION_RULE')">{{$t('action.save')}}</button>
          </template>
      </Modal>
    </transition>
  </div>
</template>
<script>
import _ from 'lodash'
import moment from 'moment'
import Calendar from '../components/exclusion-rules/Calendar'
import CheckPermission from '../components/utils/CheckPermission'
import Input from '../components/slds/Input'
import Modal from '../components/Modal'
import Picklist from '../components/slds/Picklist'

import { getErrorMessage, validate } from '../components/utils/utils'
export default {
  mixins: [CheckPermission],
  components: {
    Calendar,
    Input,
    Modal,
    Picklist
  },
  computed: {
    calendarDisabled () {
      switch (this.calendar) {
        case 'CC': {
          return !this.market || !this.channel
        }
        default:
          return true
      }
    },
    filteredTimingSlots () {
      if (!this.calendarDisabled && this.timingSlots[this.calendar]) {
        if (this.market) {
          return this.timingSlots[this.calendar].filter(rule => {
            return rule.channel === this.channel && rule.codMarket === this.market
          })
        } else if (this.country) {
          return this.timingSlots[this.calendar].filter(rule => {
            return rule.channel === this.channel && rule.codCountry === this.country
          })
        } else {
          return []
        }
      } else {
        return []
      }
    },
    timezone () {
      const settings = this.$store.getters['settings/settings']
      const { countries, timezones } = settings
      if (this.market) {
        return timezones[this.market]
      } else if (this.country) {
        const reducedCountries = countries.reduce((acc, curr) => {
          const [ key, value ] = Object.entries(curr)[0]
          acc[key] = value
          return acc
        }, {})
        return timezones[reducedCountries[this.country]]
      } else {
        return ''
      }
    }
  },
  async created () {
    try {
      this.viewMode = 'monthly'
      const settings = await this.$store.dispatch('settings/getSettings')
      this.markets = settings.calendarMarkets
      this.channels = settings.channels
      this.countries = settings.countries.map(c => Object.keys(c)).flat()
      this.calendarLocale = this.$store.getters['session/locale']

      await Promise.all([this.getTimingSlots(), this.getPressureRules()])

      this.days = _.range(7).map(i => moment().locale(this.calendarLocale).startOf('week').add(i, 'd'))
    } catch (error) {
      this.showToast('error', this.$t('general.error'), 'text', error)
    }
  },
  data () {
    return {
      country: '',
      calendars: ['CC'],
      calendar: '',
      countries: [],
      channel: '',
      channels: [],
      days: [],
      market: '',
      markets: [],
      pressureRule: {
        maxCommsWeek: '',
        maxCommsMonth: '',
        minDaysBetweenComms: ''
      },
      pressureRules: [],
      selectedSlot: {
        channel: '',
        codCountry: '',
        codMarket: '',
        inclusionDayLabel: '',
        sendStartHour: '',
        sendEndHour: '',
        type: ''
      },
      showModal: false,
      showSidebar: true,
      timingSlots: []
    }
  },
  methods: {
    asTwoDigits (value) {
      if (!value) {
        return '00'
      }
      const number = parseInt(value)
      if (number === 0) {
        return '00'
      } else if (number > 0 && number <= 9) {
        return `0${number}`
      } else {
        return `${value}`
      }
    },
    closeModal () {
      this.selectedSlot = {
        channel: '',
        codCountry: '',
        codMarket: '',
        inclusionDayLabel: '',
        sendStartHour: '',
        sendEndHour: '',
        type: ''
      }
      this.showModal = false
    },
    async deleteSlot () {
      try {
        await this.$store.dispatch('executionRules/deleteTimingSlot', this.selectedSlot)
        this.showToast('success', this.$t('general.success'), 'text', this.$t('message.delete'))
        this.closeModal()
        await this.getTimingSlots()
      } catch (error) {
        console.error(error)
        this.showToast('error', this.$t('general.error'), 'text', getErrorMessage(error))
      }
    },
    filterPressureRule () {
      if (this.market && this.channel && this.pressureRules[this.market] && this.pressureRules[this.market][this.channel]) {
        this.pressureRule = this.pressureRules[this.market][this.channel][0]
      } else {
        this.pressureRule = { maxCommsWeek: '', maxCommsMonth: '', minDaysBetweenComms: '' }
      }
    },
    async getTimingSlots () {
      this.timingSlots = await this.$store.dispatch('executionRules/getTimingSlots')
    },
    async getPressureRules () {
      this.pressureRules = await this.$store.dispatch('executionRules/getPressureRules')
    },
    handleBlur (field, event) {
      const { value } = event.target
      const split = value.split(':')
      const formatted = `${this.asTwoDigits(split[0])}:${this.asTwoDigits(split[1])}`

      this.selectedSlot[field] = formatted
    },
    handleDayClick (event) {
      if (this.checkPermission('MANAGE_EXECUTION_RULE')) {
        const { day, slot } = event

        if (slot) {
          this.selectedSlot = {
            channel: this.channel,
            codCountry: this.country,
            codMarket: this.market,
            id: slot.id,
            inclusionDayLabel: this.$t(`days.eng.long.${slot.inclusionDay}`),
            inclusionDay: slot.inclusionDay,
            sendStartHour: slot.sendStartHour.split(':').splice(0, 2).join(':'),
            sendEndHour: slot.sendEndHour.split(':').splice(0, 2).join(':'),
            type: this.calendar
          }
        } else {
          const upperCaseDay = day.toUpperCase()
          this.selectedSlot = {
            channel: this.channel,
            codCountry: this.country,
            codMarket: this.market,
            inclusionDayLabel: this.$t(`days.eng.long.${upperCaseDay}`),
            inclusionDay: upperCaseDay,
            sendStartHour: '',
            sendEndHour: '',
            type: this.calendar
          }
        }
        this.showModal = true
      }
    },
    async saveSlot () {
      try {
        const valid = validate(this, 'send_')
        if (valid) {
          const payload = {
            ...this.selectedSlot,
            sendStartHour: `${this.selectedSlot.sendStartHour}${this.timezone}`,
            sendEndHour: `${this.selectedSlot.sendEndHour}${this.timezone}`
          }
          await this.$store.dispatch('executionRules/upsertTimingSlots', payload)
          this.showToast('success', this.$t('general.success'), 'text', this.$t('message.create'))
          this.closeModal()
          await this.getTimingSlots()
        }
      } catch (error) {
        console.error(error)
        this.showToast('error', this.$t('general.error'), 'text', getErrorMessage(error))
      }
    },
    async savePressureRule () {
      try {
        const { market, channel, pressureRule } = this
        const body = { ...pressureRule, channel, codMarket: market }

        const valid = validate(this, 'pressure_')
        if (valid) {
          await this.$store.dispatch('executionRules/upsertPressureRule', body)
          this.showToast('success', this.$t('general.success'), 'text', this.$t('message.create'))
          this.closeModal()
          await this.getPressureRules()
        }
      } catch (error) {
        console.error(error)
        this.showToast('error', this.$t('general.error'), 'text', getErrorMessage(error))
      }
    },
    toggleSidebar () {
      this.showSidebar = !this.showSidebar
    }
  },
  watch: {
    calendar () {
      this.market = ''
      this.country = ''
      this.channel = ''
      
      this.filterPressureRule()
    },
    channel () {
      this.filterPressureRule()
    },
    market () {
      this.filterPressureRule()
    }
  }
}
</script>
<style lang="scss" scoped>
  .container {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  .calendar {
    flex-grow: 1;
  }

  .sidebar {
    margin-top: 10px;
  }

  .column {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
  }

  .row {
    flex-grow: 1;
    display: flex;
    flex-direction: row;

    & > .column {
      &:not(:last-child) {
        margin-right: 10px;
      }

      & > article {
        margin-top: 10px;

        &:first-child {
          flex-grow: 1;
        }
      }
    }
  }
  .slds-button-group:not(:first-child) {
    margin-left: 10px !important;
  }

  .full-height {
    height: 100%;
  }

  .sidebar {
    display: flex;
    flex-direction: column;

    & > button {
      margin-top: 10px;
      margin-bottom: 10px;
    }
  }

  .calendar-alert {
    position: absolute;
    height: 100%;
    width: 100%;
    top: 0;

    & > div {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  }

</style>
